// 📄 src/pages/Settings/WhatsAppSettings.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import PricingTimeZonePanel from "./PricingTimeZonePanel";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { requestBusinessSelection } from "../../utils/businessSelectionBus";
import { 
  Save, 
  Trash2, 
  CheckCircle, 
  Phone, 
  Plus, 
  RefreshCw, 
  Star,
  Info,
  ShieldCheck,
  Eye,
  EyeOff,
  Globe,
  Lock,
  Link,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import MetaPinActivationModal from "../../components/modals/MetaPinActivationModal";
import WhatsAppAddNumberConfirmModal from "../../components/modals/WhatsAppAddNumberConfirmModal";
import WhatsAppCapacityLimitModal from "../../components/modals/WhatsAppCapacityLimitModal";
import AutoGreetingSettingsPanel from "./AutoGreetingSettingsPanel";
import {
  getAdditionalNumberPreflight,
  hasReliableCapacityUsage,
} from "../../api/whatsAppCapacity";


// === Canonical providers (MUST match backend exactly) ===
const PROVIDERS = [
  { value: "META_CLOUD", label: "META_CLOUD" },
];

const DEFAULT_META_API_URL = "https://graph.facebook.com/v22.0";
const META_WABA_ID_RE = /^\d{5,30}$/;
const ESU_INTENT_KEY = "xb_esu_intent";
const ESU_ADDITIONAL_NUMBER_INTENT = "additionalNumber";
const ESU_PIN_TARGET_KEY = "xb_pending_esu_phone_number_id";

// --- BusinessId helper (used only as a sanity check / legacy headers) ---
const TOKEN_KEY = "xbyte_token";
const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getBusinessIdFromStorage() {
  try {
    // Keep in sync with AuthProvider / axiosClient
    const keys = ["sa_selectedBusinessId", "businessId", "business_id"];
    for (const key of keys) {
      const saved = localStorage.getItem(key);
      if (saved && GUID_RE.test(saved)) return saved;
    }
    return null;
  } catch {
    return null;
  }
}

function getBusinessIdFromJwt() {
  try {
    const jwt = localStorage.getItem(TOKEN_KEY);
    if (!jwt) return null;

    const [, payloadB64] = jwt.split(".");
    if (!payloadB64) return null;

    const payload = JSON.parse(
      atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))
    );

    const bid =
      payload?.BusinessId ||
      payload?.businessId ||
      payload?.biz ||
      payload?.bid ||
      null;

    return typeof bid === "string" && GUID_RE.test(bid) ? bid : null;
  } catch {
    return null;
  }
}

function resolveBusinessId(effectiveBusinessIdFromContext) {
  if (
    typeof effectiveBusinessIdFromContext === "string" &&
    GUID_RE.test(effectiveBusinessIdFromContext)
  ) {
    return effectiveBusinessIdFromContext;
  }
  return getBusinessIdFromStorage() || getBusinessIdFromJwt();
}

// Normalize provider names
const normalizeProvider = p => {
  const raw = (p ?? "").toString().trim();
  if (!raw) return "META_CLOUD";
  const up = raw.toUpperCase();
  if (
    up === "META_CLOUD" ||
    up === "META" ||
    up === "METACLOUD" ||
    up === "META-CLOUD"
  ) {
    return "META_CLOUD";
  }
  return "META_CLOUD";
};

// UI label per provider (still binds to apiKey field)
const secretLabelFor = () => "Token";

// Initial blank global settings
const blank = {
  provider: "META_CLOUD",
  apiUrl: DEFAULT_META_API_URL,
  apiKey: "",
  wabaId: "",
  senderDisplayName: "",
  webhookSecret: "",
  webhookVerifyToken: "",
  webhookCallbackUrl: "",
  isActive: true,
};

function normalizeApiUrlForForm(apiUrl) {
  const value = (apiUrl || "").trim();
  return value || DEFAULT_META_API_URL;
}

function normalizeWabaIdForForm(wabaId) {
  const value = (wabaId || "").trim();
  return META_WABA_ID_RE.test(value) ? value : "";
}

function normalizeApiKeyForForm(apiKey) {
  const value = (apiKey || "").trim();
  if (!value || value.length < 24) return "";
  if (value.includes("@") || /\s/.test(value)) return "";
  return value;
}

function toDraftFormData(formData) {
  return {
    ...formData,
    apiUrl: normalizeApiUrlForForm(formData?.apiUrl),
    apiKey: normalizeApiKeyForForm(formData?.apiKey),
    wabaId: normalizeWabaIdForForm(formData?.wabaId),
  };
}

export default function WhatsAppSettings() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const effectiveBusinessIdFromContext = auth?.effectiveBusinessId || null;

  const [formData, setFormData] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [pricingTimezoneStatus, setPricingTimezoneStatus] = useState(null);
  const [pricingTimezoneRevision, setPricingTimezoneRevision] = useState(0);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState("");
  const [senders, setSenders] = useState([]);
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);
  const [hasStoredApiKey, setHasStoredApiKey] = useState(false);
  const [savedProvider, setSavedProvider] = useState(null);
  const [showApiKey, setShowApiKey] = useState(true);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [expandedAutoGreetingKey, setExpandedAutoGreetingKey] = useState(null);

  // ESU status (normalized)
  const [esuStatus, setEsuStatus] = useState({
    loading: true,
    hasEsuFlag: false,
    hasValidToken: false,
    willExpireSoon: false,
    tokenExpiresAtUtc: null,
    isConfiguredViaEsu: false,
    isTokenExpiredOrInvalid: false,
    isTokenExpiringSoon: false,
    isFullyHealthy: false,
    phoneCount: 0,
    hasWaba: false,
    debug: null,
  });

  const [showPinModal, setShowPinModal] = useState(() => {
    return !searchParams.has("esuStatus") &&
      sessionStorage.getItem("xb_pending_esu_pin") === "true";
  });
  const [pinPhoneNumberId, setPinPhoneNumberId] = useState(
    () => sessionStorage.getItem(ESU_PIN_TARGET_KEY) || ""
  );

  const closePinModal = () => {
    setShowPinModal(false);
    sessionStorage.removeItem("xb_pending_esu_pin");
    sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
    setPinPhoneNumberId("");
  };

  const [connectingEsu, setConnectingEsu] = useState(false);
  const [connectingAdditionalNumber, setConnectingAdditionalNumber] = useState(false);
  const additionalNumberClickRef = useRef(false);
  const additionalNumberContinueRef = useRef(false);
  const [capacityPreflight, setCapacityPreflight] = useState(null);
  const [showCapacityLimitModal, setShowCapacityLimitModal] = useState(false);
  const [showAddNumberConfirmModal, setShowAddNumberConfirmModal] = useState(false);

  const businessId = useMemo(
    () => resolveBusinessId(effectiveBusinessIdFromContext),
    [effectiveBusinessIdFromContext]
  );
  const hasBusinessContext = !!businessId;
  const canManageWhatsApp =
    !!auth?.hasAllAccess ||
    !!auth?.hasFeature?.("SETTINGS.META.ACCOUNT.MANAGEMENT");
  const refreshCapacityPreflight = useCallback(async () => {
    try {
      const result = await getAdditionalNumberPreflight();
      setCapacityPreflight(result);
      return result;
    } catch {
      setCapacityPreflight(null);
      return null;
    }
  }, []);
  const requestBusinessContextSelection = () =>
    requestBusinessSelection({
      reason: "business-context",
      message: "Select a business from the topbar to continue.",
      source: window.location.pathname,
      open: false,
    });

  const withBiz = useCallback(
    (cfg = {}) =>
      businessId
        ? {
            ...cfg,
            headers: { ...(cfg.headers || {}), "X-Business-Id": businessId },
          }
        : cfg,
    [businessId]
  );

  // ===== Draft persistence (prevents "fields disappear" on navigation/tab switch) =====
  const draftKey = useCallback(
    () => `xb_whatsapp_settings_draft:v1:${businessId || "no-biz"}`,
    [businessId]
  );
  const DRAFT_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours
  const [canPersistDraft, setCanPersistDraft] = useState(false);
  const hasUserEditedRef = useRef(false);
  const prevBizIdRef = useRef(businessId);

  const restoreFromDraft = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(draftKey());
      if (!raw) return false;

      const data = JSON.parse(raw);
      if (!data || data.v !== 1) return false;
      if (data.businessId !== businessId) return false;
      if (data.at && Date.now() - new Date(data.at).getTime() > DRAFT_TTL_MS) {
        sessionStorage.removeItem(draftKey());
        return false;
      }

      if (data.formData && typeof data.formData === "object") {
        setFormData(p => ({ ...p, ...toDraftFormData(data.formData) }));
      }
      if (typeof data.hasSavedOnce === "boolean") setHasSavedOnce(data.hasSavedOnce);
      if (typeof data.hasStoredApiKey === "boolean") {
        setHasStoredApiKey(data.hasStoredApiKey);
      }
      if (typeof data.savedProvider === "string") setSavedProvider(data.savedProvider);

      return true;
    } catch {
      return false;
    }
  }, [DRAFT_TTL_MS, businessId, draftKey]);

  // ===== Numbers API helpers =====
  const listNumbers = useCallback(async provider => {
    const p = normalizeProvider(provider);
    const { data } = await axiosClient.get(
      `/whatsappsettings/${p}/numbers`,
      withBiz()
    );
    return Array.isArray(data) ? data : [];
  }, [withBiz]);

  const upsertNumber = async (provider, row) => {
    const p = normalizeProvider(provider);
    const payload = {
      phoneNumberId: (row.phoneNumberId || "").trim(),
      whatsAppBusinessNumber: (row.whatsAppBusinessNumber || "").trim(),
      senderDisplayName: (row.label || row.senderDisplayName || "").trim(),
      isActive: row.isActive ?? true,
      isDefault: !!row.isDefault,
    };
    const { data } = await axiosClient.post(
      `/whatsappsettings/${p}/numbers`,
      payload,
      withBiz()
    );
    return data;
  };

  const deleteNumber = async (provider, id) => {
    const p = normalizeProvider(provider);
    await axiosClient.delete(`/whatsappsettings/${p}/numbers/${id}`, withBiz());
  };

  const setDefaultNumber = async (provider, id) => {
    const p = normalizeProvider(provider);
    await axiosClient.patch(
      `/whatsappsettings/${p}/numbers/${id}/default`,
      null,
      withBiz()
    );
  };

  const fetchNumbers = useCallback(async provider => {
    try {
      const items = await listNumbers(provider);
      setSenders(
        items.map(n => ({
          id: n.id,
          label: n.senderDisplayName || "",
          verifiedName: n.verifiedName || "",
          phoneNumberId: n.phoneNumberId || "",
          whatsAppBusinessNumber: n.whatsAppBusinessNumber || "",
          status: n.status || "",
          qualityRating: n.qualityRating || "",
          nameStatus: n.nameStatus || "",
          isDefault: !!n.isDefault,
          isActive: n.isActive ?? true,
        }))
      );
    } catch {
      setSenders([]);
    }
  }, [listNumbers]);

  // ===== Derived UI =====
  const providerLabel = useMemo(
    () => secretLabelFor(formData.provider),
    [formData.provider]
  );
  const selectedProvider = useMemo(
    () => normalizeProvider(formData.provider),
    [formData.provider]
  );
  const showFetchButton = hasSavedOnce;
  const hasAnyManualSender = useMemo(
    () =>
      senders.some(
        row =>
          (row?.isActive ?? true) &&
          (!!(row?.phoneNumberId || "").trim() ||
            !!(row?.whatsAppBusinessNumber || "").trim())
      ),
    [senders]
  );
  const isManualConnectionReady =
    !!hasSavedOnce &&
    (hasStoredApiKey || !!(formData.apiKey || "").trim()) &&
    !!(formData.wabaId || "").trim() &&
    hasAnyManualSender &&
    !!formData.isActive;
  const isUiConnected = esuStatus.isFullyHealthy || isManualConnectionReady;
  const isManualConnectedOnly = isManualConnectionReady && !esuStatus.isFullyHealthy;

  // Restore draft immediately on biz change (or first mount)
  useEffect(() => {
    const bizChanged =
      prevBizIdRef.current !== undefined && prevBizIdRef.current !== businessId;
    prevBizIdRef.current = businessId;

    hasUserEditedRef.current = false;
    setCanPersistDraft(false);

    const restored = restoreFromDraft();
    if (restored) {
      hasUserEditedRef.current = true; // treat restored state as user-owned
      setCanPersistDraft(true);
      return;
    }

    if (bizChanged) {
      setFormData(blank);
      setSenders([]);
      setHasSavedOnce(false);
      setHasStoredApiKey(false);
      setSavedProvider(null);
    }
  }, [businessId, restoreFromDraft]);

  // Persist draft (debounced) so tab/menu switching doesn't wipe inputs
  useEffect(() => {
    if (!canPersistDraft) return;

    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(
          draftKey(),
          JSON.stringify({
            v: 1,
            businessId,
            at: new Date().toISOString(),
            formData: toDraftFormData(formData),
            hasSavedOnce,
            hasStoredApiKey,
            savedProvider,
          })
        );
      } catch {
        // ignore
      }
    }, 350);

    return () => clearTimeout(t);
  }, [businessId, canPersistDraft, draftKey, formData, hasSavedOnce, hasStoredApiKey, savedProvider]);

  const loadStatus = useCallback(async () => {
    try {
      setEsuStatus(s => ({ ...s, loading: true }));

      // Backend: GET /api/esu/facebook/status (uses JWT for businessId)
      const res = await axiosClient.get("esu/facebook/status");
      const payload = res?.data ?? {};
      const dto = payload?.data || payload?.Data || payload || {};

      const hasEsuFlag =
        dto.hasEsuFlag ?? dto.HasEsuFlag ?? dto.facebookEsuCompleted ?? false;

      const tokenExpiresAtUtc =
        dto.tokenExpiresAtUtc ?? dto.TokenExpiresAtUtc ?? null;

      const hasValidToken =
        dto.hasValidToken ??
        dto.HasValidToken ??
        (tokenExpiresAtUtc ? true : false);

      const willExpireSoon =
        dto.willExpireSoon ??
        dto.WillExpireSoon ??
        dto.isExpiringSoon ??
        dto.IsExpiringSoon ??
        false;

      const isConfiguredViaEsu = !!hasEsuFlag;
      const isTokenExpiredOrInvalid = isConfiguredViaEsu && !hasValidToken;
      const isTokenExpiringSoon = !!hasValidToken && willExpireSoon;
      const isFullyHealthy =
        isConfiguredViaEsu && hasValidToken && !willExpireSoon;

      const phoneCount = dto.phoneCount || dto.numbersCount || 0;
      const hasWaba = !!(dto.wabaId || dto.WabaId);
      const debug = dto.debug ?? dto.Debug ?? null;

      setEsuStatus({
        loading: false,
        hasEsuFlag,
        hasValidToken,
        willExpireSoon,
        tokenExpiresAtUtc,
        isConfiguredViaEsu,
        isTokenExpiredOrInvalid,
        isTokenExpiringSoon,
        isFullyHealthy,
        phoneCount,
        hasWaba,
        debug,
      });
    } catch (err) {
      console.error("Unable to load Meta ESU status", err);
      setEsuStatus({
        loading: false,
        hasEsuFlag: false,
        hasValidToken: false,
        willExpireSoon: false,
        tokenExpiresAtUtc: null,
        isConfiguredViaEsu: false,
        isTokenExpiredOrInvalid: false,
        isTokenExpiringSoon: false,
        isFullyHealthy: false,
        phoneCount: 0,
        hasWaba: false,
        debug: "status-error",
      });
    }
  }, []);

  // ===== ESU: load connection status (JWT-based) =====
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  // ===== Initial load of saved settings + numbers =====
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const res = await axiosClient.get("/whatsappsettings/me", withBiz());
        if (!mounted) return;

        const body = res?.data || {};

        // Support both:
        // 1) New shape: { ok, hasSettings, data }
        // 2) Legacy shape: settings object directly
        const hasSettingsFlag =
          typeof body.hasSettings === "boolean" ? body.hasSettings : undefined;

        const settings =
          hasSettingsFlag === false
            ? null
            : body.data !== undefined
            ? body.data
            : body;

        // No settings configured yet (expected state)
        if (!settings || Object.keys(settings).length === 0) {
          // UX hint (safe even if removed later; logic below still holds)
          toast.info("No WhatsApp settings found. You can create them now.");
          setFormData(p => ({
            ...p,
            provider: normalizeProvider(p.provider),
            apiUrl: normalizeApiUrlForForm(p.apiUrl),
            apiKey: "",
            wabaId: normalizeWabaIdForForm(p.wabaId),
          }));
          setHasSavedOnce(false);
          setHasStoredApiKey(false);
          setSavedProvider(null);
          setSenders([]);
          setCanPersistDraft(true);
          return;
        }

        const provider = normalizeProvider(settings.provider);
        const apiKeyFromServer = normalizeApiKeyForForm(
          settings.apiKey || settings.apiToken
        );
        const storedApiKey = !!(
          settings.hasApiKey ||
          settings.hasApiToken ||
          apiKeyFromServer
        );

        setFormData(prev => ({
          ...prev,
          provider: provider || prev.provider,
          apiUrl: normalizeApiUrlForForm(settings.apiUrl || prev.apiUrl),
          apiKey: apiKeyFromServer,
          wabaId: normalizeWabaIdForForm(settings.wabaId),
          senderDisplayName: settings.senderDisplayName || prev.senderDisplayName || "",
          webhookSecret: settings.webhookSecret || prev.webhookSecret || "",
          webhookVerifyToken:
            settings.webhookVerifyToken || prev.webhookVerifyToken || "",
          webhookCallbackUrl:
            settings.webhookCallbackUrl || prev.webhookCallbackUrl || "",
          isActive: settings.isActive ?? prev.isActive ?? true,
        }));

        setSavedProvider(provider);

        const existed =
          !!settings.provider ||
          storedApiKey ||
          !!normalizeWabaIdForForm(settings.wabaId);
        setHasSavedOnce(!!existed);
        setHasStoredApiKey(storedApiKey);
        setCanPersistDraft(true);

        await fetchNumbers(provider);
      } catch (err) {
        // Real errors (network, 500, etc.) will already be surfaced
        // by the global axiosClient interceptor.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("Failed to load WhatsApp settings", err);
        }
        setHasSavedOnce(false);
        setHasStoredApiKey(false);
        setSavedProvider(null);
      } finally {
        // no-op
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // // ===== Initial load of saved settings + numbers =====
  // useEffect(() => {
  //   let mounted = true;
  //   (async () => {
  //     try {
  //       setLoading(true);
  //       const res = await axiosClient.get("/whatsappsettings/me", withBiz());
  //       if (!mounted) return;
  //       const data = res?.data ?? {};

  //       const provider = normalizeProvider(data?.provider);
  //       const secret = data?.apiKey || data?.apiToken || "";

  //       setFormData(prev => ({
  //         ...prev,
  //         provider,
  //         apiUrl: data?.apiUrl || "",
  //         apiKey: secret,
  //         wabaId: data?.wabaId || "",
  //         senderDisplayName: data?.senderDisplayName || "",
  //         webhookSecret: data?.webhookSecret || "",
  //         webhookVerifyToken: data?.webhookVerifyToken || "",
  //         webhookCallbackUrl: data?.webhookCallbackUrl || "",
  //         isActive: data?.isActive ?? true,
  //       }));

  //       setSavedProvider(provider);

  //       const existed =
  //         !!data?.provider ||
  //         !!data?.apiKey ||
  //         !!data?.apiToken ||
  //         !!data?.wabaId;
  //       setHasSavedOnce(!!existed);

  //       await fetchNumbers(provider);
  //     } catch {
  //       toast.info("ℹ️ No WhatsApp settings found. You can create them now.");
  //       setHasSavedOnce(false);
  //       setSavedProvider(null);
  //     } finally {
  //       mounted && setLoading(false);
  //     }
  //   })();

  //   return () => {
  //     mounted = false;
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // Refresh numbers when dropdown provider changes
  useEffect(() => {
    fetchNumbers(formData.provider);
  }, [fetchNumbers, formData.provider]);

  useEffect(() => {
    if (canManageWhatsApp && esuStatus.isConfiguredViaEsu && (formData.wabaId || "").trim()) {
      void refreshCapacityPreflight();
    } else {
      setCapacityPreflight(null);
    }
  }, [canManageWhatsApp, esuStatus.isConfiguredViaEsu, formData.wabaId, refreshCapacityPreflight]);

  // Handle ESU results from URL + sessionStorage persistence
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawStatus = params.get("esuStatus");
    const callbackStatus = rawStatus?.toLowerCase();
    const callbackError = params.get("error");
    const callbackPhoneNumberId = params.get("phoneNumberId") || "";
    const rawConnected = params.get("connected");
    const isAdditionalNumber =
      sessionStorage.getItem(ESU_INTENT_KEY) === ESU_ADDITIONAL_NUMBER_INTENT;

    if (callbackStatus === "success") {
      sessionStorage.removeItem("xb_pending_esu_pin");
      sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
      sessionStorage.removeItem(ESU_INTENT_KEY);
      setPinPhoneNumberId("");
      setShowPinModal(false);
      void (async () => {
        try {
          const res = await axiosClient.post(
            "/whatsappsettings/fetch-numbers",
            {},
            withBiz()
          );
          const serverBucket = normalizeProvider(res?.data?.provider);
          const fallbackBucket = savedProvider || selectedProvider;
          const bucket = serverBucket || fallbackBucket;

          await fetchNumbers(bucket);
          void refreshCapacityPreflight();

          if (bucket && bucket !== selectedProvider) {
            setFormData(p => ({ ...p, provider: bucket }));
          }
          toast.success(
            isAdditionalNumber
              ? "WhatsApp number added successfully."
              : "Connected to Meta successfully."
          );
        } catch {
          toast.warn(
            "Meta onboarding completed, but the sender list could not be refreshed. Try Sync from Meta."
          );
        } finally {
          loadStatus();
        }
      })();
    } 
    else if (callbackStatus === "needs_pin" || (!rawStatus && sessionStorage.getItem("xb_pending_esu_pin") === "true")) {
      if (callbackStatus === "needs_pin") {
        if (callbackPhoneNumberId) {
          sessionStorage.setItem(ESU_PIN_TARGET_KEY, callbackPhoneNumberId);
          setPinPhoneNumberId(callbackPhoneNumberId);
        }
        toast.info("Enter the existing two-step verification PIN for this WhatsApp number.");
        sessionStorage.setItem("xb_pending_esu_pin", "true");
        sessionStorage.removeItem(ESU_INTENT_KEY);
      }
      setShowPinModal(true);
    } else if (callbackStatus === "failed") {
      sessionStorage.removeItem(ESU_INTENT_KEY);
      sessionStorage.removeItem("xb_pending_esu_pin");
      sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
      setPinPhoneNumberId("");
      setShowPinModal(false);
      if (callbackError === "ADDITIONAL_NUMBER_WABA_MISMATCH") {
        toast.error(
          "This number belongs to a different WhatsApp Business Account. Select a number from the same account already connected to this workspace."
        );
      } else {
        toast.error("Meta onboarding could not be completed. Please try again.");
      }
      void loadStatus();
      void fetchNumbers(selectedProvider);
    }

    // Clean up URL params
    if (rawStatus || rawConnected || callbackError || callbackPhoneNumberId) {
      const next = new URLSearchParams(searchParams);
      next.delete("esuStatus");
      next.delete("connected");
      next.delete("error");
      next.delete("phoneNumberId");
      setSearchParams(next, { replace: true });
    }
  }, [fetchNumbers, loadStatus, refreshCapacityPreflight, savedProvider, searchParams, selectedProvider, setSearchParams, withBiz]);

  const handlePinSuccess = async () => {
    sessionStorage.removeItem("xb_pending_esu_pin");
    sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
    sessionStorage.removeItem(ESU_INTENT_KEY);
    setPinPhoneNumberId("");
    await fetchNumbers(selectedProvider);
    await loadStatus();
  };

  const addSender = () =>
    setSenders(s => [
      ...s,
      {
        label: "",
        phoneNumberId: "",
        whatsAppBusinessNumber: "",
        isDefault: s.length === 0,
        isActive: true,
      },
    ]);

  const removeSender = idx => setSenders(s => s.filter((_, i) => i !== idx));

  const updateSender = (idx, key, value) =>
    setSenders(s =>
      s.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    );

  const autoGreetingRowKey = (row, idx) =>
    (row?.phoneNumberId || "").trim() || row?.id || `sender-${idx}`;

  const toggleAutoGreetingPanel = (row, idx) => {
    const key = autoGreetingRowKey(row, idx);
    setExpandedAutoGreetingKey(current => (current === key ? null : key));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    hasUserEditedRef.current = true;
    setCanPersistDraft(true);
    setFormData(p => ({ ...p, [name]: value }));

    // Clear field specific error when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleToggle = e => {
    const { name, checked } = e.target;
    hasUserEditedRef.current = true;
    setCanPersistDraft(true);
    setFormData(p => ({ ...p, [name]: checked }));
  };

  const handleProviderChange = e => {
    const provider = normalizeProvider(e.target.value);
    hasUserEditedRef.current = true;
    setCanPersistDraft(true);
    setFormData(p => ({ ...p, provider }));
  };

  // ===== Save global settings =====
  const validateBeforeSave = () => {
    if (!hasStoredApiKey && !formData.apiKey.trim()) {
      toast.error("API Key / Token is required.");
      return false;
    }
    return true;
  };

  const buildSettingsPayload = () => ({
    provider: normalizeProvider(formData.provider),
    apiUrl: normalizeApiUrlForForm(formData.apiUrl),
    apiKey: (formData.apiKey || "").trim() || null,
    wabaId: (formData.wabaId || "").trim() || null,
    senderDisplayName: (formData.senderDisplayName || "").trim() || null,
    webhookSecret: (formData.webhookSecret || "").trim() || null,
    webhookVerifyToken: (formData.webhookVerifyToken || "").trim() || null,
    webhookCallbackUrl: (formData.webhookCallbackUrl || "").trim() || null,
    isActive: !!formData.isActive,
  });

  const handleSaveGlobal = async () => {
    if (!validateBeforeSave()) return;
    try {
      setSaving(true);
      const nextApiKey = (formData.apiKey || "").trim();
      const payload = buildSettingsPayload();

      await axiosClient.put("/whatsappsettings/update", payload, withBiz());
      setPricingTimezoneRevision(value => value + 1);

      setHasSavedOnce(true);
      if (nextApiKey) {
        setHasStoredApiKey(true);
      }
      setSavedProvider(payload.provider);
      setCanPersistDraft(true);

      toast.success("Settings saved.");
      setFieldErrors({}); // Clear all errors on success
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save settings.";
      
      // Inline error for WABA ID
      if (msg.includes("WABA ID")) {
        setFieldErrors(prev => ({ ...prev, wabaId: msg }));
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // ===== Test connection using backend "current" settings =====
  const handleTest = async () => {
    setTesting(true);
    setTestResult("");
    try {
      const res = await axiosClient.post(
        "/whatsappsettings/test-connection/current",
        {},
        withBiz()
      );
      setTestResult(JSON.stringify(res?.data ?? {}, null, 2));
      toast.success(res?.data?.message || "Connection test complete.");
    } catch (err) {
      setTestResult(
        JSON.stringify(err?.response?.data ?? { error: String(err) }, null, 2)
      );
      toast.error(err?.response?.data?.message || "Connection test failed.");
    } finally {
      setTesting(false);
    }
  };

  // ===== ESU: start Embedded Signup / Refresh Token from Settings page =====
  const startFacebookEsu = async () => {
    if (!hasBusinessContext) {
      requestBusinessContextSelection();
      return;
    }

    try {
      setConnectingEsu(true);

      const returnUrlAfterSuccess = "/app/settings/whatsapp";

      // JWT-based; no X-Business-Id here
      const res = await axiosClient.post("esu/facebook/start", {
        returnUrlAfterSuccess,
      });

      const authUrl =
        res?.data?.data?.authUrl ||
        res?.data?.authUrl ||
        res?.data?.url ||
        res?.data?.Data?.AuthUrl;

      if (!authUrl) {
        toast.error(
          res?.data?.message || "Could not get Meta Embedded Signup URL."
        );
        return;
      }

      window.location.href = authUrl;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to start Meta Embedded Signup."
      );
    } finally {
      setConnectingEsu(false);
    }
  };

  const startAdditionalNumberEsu = async () => {
    if (!hasBusinessContext) {
      requestBusinessContextSelection();
      return;
    }

    if (!esuStatus.isConfiguredViaEsu || !(formData.wabaId || "").trim()) {
      toast.error("Connect a Meta WhatsApp Business Account before adding another number.");
      return;
    }

    if (additionalNumberClickRef.current) return;
    additionalNumberClickRef.current = true;
    setConnectingAdditionalNumber(true);

    try {
      // Only an explicit exhausted result blocks onboarding. Unknown,
      // malformed responses, and request failures intentionally fail open.
      const preflight = await refreshCapacityPreflight();
      if (preflight?.status === "exhausted") {
        setShowCapacityLimitModal(true);
        return;
      }
      setShowAddNumberConfirmModal(true);
    } finally {
      additionalNumberClickRef.current = false;
      setConnectingAdditionalNumber(false);
    }
  };

  const continueAdditionalNumberEsu = async () => {
    if (additionalNumberContinueRef.current) return;
    additionalNumberContinueRef.current = true;
    setConnectingAdditionalNumber(true);

    try {
      sessionStorage.setItem(ESU_INTENT_KEY, ESU_ADDITIONAL_NUMBER_INTENT);
      const res = await axiosClient.post("esu/facebook/start", {
        returnUrlAfterSuccess: "/app/settings/whatsapp",
        additionalNumber: true,
      });
      const authUrl =
        res?.data?.data?.authUrl ||
        res?.data?.authUrl ||
        res?.data?.url ||
        res?.data?.Data?.AuthUrl;

      if (!authUrl) {
        sessionStorage.removeItem(ESU_INTENT_KEY);
        toast.error(res?.data?.message || "Could not start Meta Embedded Signup.");
        return;
      }
      window.location.href = authUrl;
    } catch (err) {
      sessionStorage.removeItem(ESU_INTENT_KEY);
      toast.error(err?.response?.data?.message || "Failed to start Meta Embedded Signup.");
    } finally {
      additionalNumberContinueRef.current = false;
      setConnectingAdditionalNumber(false);
    }
  };

  // ===== Fetch numbers via backend sync =====
  const handleFetchFromMeta = async () => {
    let hasErr = false;
    if (!hasStoredApiKey && !formData.apiKey?.trim()) {
      toast.warn("Please provide API Key/Token.");
      hasErr = true;
    }
    if (!formData.wabaId?.trim()) {
      setFieldErrors(prev => ({ ...prev, wabaId: "WABA ID is required to fetch numbers." }));
      hasErr = true;
    }
    if (hasErr) return;

    try {
      setFetchingMeta(true);
      const payload = buildSettingsPayload();
      await axiosClient.put("/whatsappsettings/update", payload, withBiz());
      setHasSavedOnce(true);
      if (payload.apiKey) setHasStoredApiKey(true);
      setSavedProvider(payload.provider);

      const res = await axiosClient.post(
        "/whatsappsettings/fetch-numbers",
        {},
        withBiz()
      );

      const serverBucket = normalizeProvider(res?.data?.provider);
      setPricingTimezoneRevision(value => value + 1);
      const fallbackBucket = savedProvider || selectedProvider;
      const bucket = serverBucket || fallbackBucket;

      await fetchNumbers(bucket);
      void refreshCapacityPreflight();

      if (bucket && bucket !== selectedProvider) {
        setFormData(p => ({ ...p, provider: bucket }));
      }

      const { added = 0, updated = 0, total = 0 } = res?.data || {};
      toast.success(
        `Synced — added ${added}, updated ${updated}, total ${total} (${bucket}).`
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Fetch failed.";
      
      if (msg.includes("WABA ID")) {
        setFieldErrors(prev => ({ ...prev, wabaId: msg }));
      } else {
        toast.error(msg);
      }
    } finally {
      setFetchingMeta(false);
    }
  };

  // ===== Connection Status card UI (ESU-aware) =====
  const renderConnectionStatus = () => {
    const {
      loading,
      isConfiguredViaEsu,
      isTokenExpiredOrInvalid,
      isTokenExpiringSoon,
      isFullyHealthy,
      phoneCount,
      tokenExpiresAtUtc,
    } = esuStatus;

    const formattedExpiry = tokenExpiresAtUtc
      ? new Date(tokenExpiresAtUtc).toLocaleString()
      : null;

    if (loading) {
      return (
        <div className="mb-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-600">
          Checking Meta connection status…
        </div>
      );
    }

    // Not configured via ESU, but manually configured and active
    if (!isConfiguredViaEsu && isManualConnectionReady) {
      return (
        <div className="mb-6 rounded-xl border-l-4 border-emerald-500 bg-emerald-50/40 shadow-sm overflow-hidden transition-all">
          <div className="px-5 py-3.5 flex items-start gap-4">
            <div className="mt-0.5 p-1.5 bg-emerald-100 rounded-lg text-emerald-600">
              <CheckCircle size={18} />
            </div>
            <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-emerald-900">Connected</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider border border-emerald-200">
                    Manual Setup
                  </span>
                </div>
                <span className="text-xs text-emerald-700/70 font-medium">
                  Active via manual API credentials and sender configuration.
                </span>
              </div>
              <button
                type="button"
                onClick={startFacebookEsu}
                disabled={connectingEsu || !hasBusinessContext}
                className="shrink-0 inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-md shadow-emerald-500/10 transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap"
              >
                {connectingEsu ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Connect via Facebook
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Not configured via ESU at all
    if (!isConfiguredViaEsu) {
      return (
        <div className="mb-6 rounded-xl border-l-4 border-rose-500 bg-rose-50/40 shadow-sm overflow-hidden transition-all">
          <div className="px-5 py-3.5 flex items-start gap-4">
            <div className="mt-0.5 p-1.5 bg-rose-100 rounded-lg text-rose-600">
              <ShieldCheck size={18} />
            </div>
            <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-rose-900">Disconnected</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                </div>
                <span className="text-xs text-rose-700/70 font-medium">
                  Connect via Facebook to start sending messages and more.
                </span>
              </div>
              <button
                type="button"
                onClick={startFacebookEsu}
                disabled={connectingEsu || !hasBusinessContext}
                className="shrink-0 inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-md shadow-emerald-500/10 transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap"
              >
                {connectingEsu ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Connect via Facebook
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Configured via ESU
    return (
      <div className="mb-6 rounded-xl border-l-4 border-emerald-500 bg-emerald-50/40 shadow-sm overflow-hidden transition-all">
        <div className="px-5 py-3.5 flex items-start gap-4">
          <div className="mt-0.5 p-1.5 bg-emerald-100 rounded-lg text-emerald-600">
            <CheckCircle size={18} />
          </div>
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-emerald-900">Connected</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                </div>
                {isFullyHealthy && (
                  <span className="text-xs text-emerald-700/70 font-medium">
                    {pricingTimezoneStatus === "Configured" ? "WhatsApp connection is active for this workspace" : "WhatsApp connected; billing setup needs verification"}
                    {phoneCount ? ` • ${phoneCount} numbers synced` : ""}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={startFacebookEsu}
                  disabled={connectingEsu || !hasBusinessContext}
                  className="shrink-0 inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 shadow-md shadow-emerald-600/10 transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap"
                >
                  {connectingEsu ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : isTokenExpiredOrInvalid || isTokenExpiringSoon ? (
                    "Refresh Token"
                  ) : (
                    "Manage Connection"
                  )}
                </button>
                
                {formattedExpiry && (
                  <span className="text-[9px] text-slate-400 font-mono uppercase tracking-wider whitespace-nowrap">
                    Expires: {formattedExpiry}
                  </span>
                )}
              </div>
            </div>

            {isTokenExpiredOrInvalid && (
              <div className="mt-2 text-[10px] text-rose-700 bg-white/60 border border-rose-100 px-3 py-1.5 rounded-lg flex items-center gap-2">
                <Info size={12} className="shrink-0" />
                <p><b>Token Expired:</b> Generate a new long-lived token using <b>Refresh Token</b> below.</p>
              </div>
            )}

            {isTokenExpiringSoon && (
              <div className="mt-2 text-[10px] text-amber-700 bg-white/60 border border-amber-100 px-3 py-1.5 rounded-lg flex items-center gap-2">
                <Info size={12} className="shrink-0" />
                <p>Your Meta access token will expire soon. Please refresh it to avoid service gaps.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ===== Render =====
  return (
    <div className="bg-[#f5f6f7] min-h-[calc(100vh-80px)]">
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-8">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600 shadow-sm shadow-emerald-200">
            <Phone size={16} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2.5 mb-0.5">
              <h1 className="text-xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                API Setup
              </h1>
              {isUiConnected ? (
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100 shadow-sm transition-all hover:bg-emerald-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                  <span className="text-[9px] font-bold uppercase tracking-wider">Connected</span>
                  {isManualConnectedOnly && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700/80">
                      Manual
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-700 rounded-lg border border-rose-100 shadow-sm transition-all hover:bg-rose-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)] animate-pulse" />
                  <span className="text-[9px] font-bold uppercase tracking-wider">Disconnected</span>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 max-w-2xl">
              Configure your API credentials and manage sender identities.
            </p>
          </div>
        </div>

        <div className="flex items-center bg-slate-100/30 p-1 rounded-xl border border-slate-200/50 backdrop-blur-sm shadow-sm transition-all">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing}
            className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-xs font-bold transition-all border-none ${
              testing
                ? "bg-transparent text-slate-300"
                : "bg-transparent text-slate-600 hover:text-slate-900 active:scale-95"
            }`}
          >
            {testing ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Test
          </button>
          
          <div className="w-[1px] h-4 bg-slate-200/60 mx-1" />

          <button
            type="button"
            onClick={handleSaveGlobal}
            disabled={saving}
            className={`inline-flex items-center justify-center px-5 py-2 rounded-lg text-xs font-bold shadow-lg transition-all ${
              saving
                ? "bg-slate-400 text-white cursor-not-allowed"
                : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-500/20 active:scale-95"
            }`}
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        {renderConnectionStatus()}
        {hasBusinessContext && <PricingTimeZonePanel key={businessId} businessId={businessId}
          canManage={canManageWhatsApp} revision={pricingTimezoneRevision}
          onStatusChange={setPricingTimezoneStatus} />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* API Credentials Card */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all hover:border-slate-300/80">
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
              <div className="p-1.5 bg-blue-100 rounded-lg text-blue-600">
                <Lock size={16} />
              </div>
              <h3 className="text-sm font-bold text-slate-800">API Credentials</h3>
            </div>
            
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Provider</label>
                  <select
                    name="provider"
                    value={normalizeProvider(formData.provider)}
                    onChange={handleProviderChange}
                    className="w-full px-3.5 py-1.5 rounded-lg text-xs border border-slate-200 bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 transition-all font-medium"
                  >
                    {PROVIDERS.map(p => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">API URL</label>
                  <div className="relative flex items-center">
                    <div className="absolute left-3 text-slate-400 pointer-events-none">
                      <Globe size={13} />
                    </div>
                    <input
                      type="text"
                      name="apiUrl"
                      value={formData.apiUrl}
                      onChange={handleChange}
                      placeholder="https://graph.facebook.com/v22.0"
                      className="w-full pl-9 pr-4 py-1.5 rounded-lg text-xs border border-slate-200 bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">{providerLabel}</label>
                  <div className="relative flex items-center">
                    <div className="absolute left-3 text-slate-400 pointer-events-none">
                      <Lock size={13} />
                    </div>
                    <input
                      type={showApiKey ? "text" : "password"}
                      name="apiKey"
                      value={formData.apiKey}
                      onChange={handleChange}
                      placeholder={
                        hasStoredApiKey
                          ? "Paste or edit Meta access token"
                          : "00000000-0000-0000-0000-000000000000"
                      }
                      className="w-full pl-9 pr-12 py-1.5 rounded-lg text-xs border border-slate-200 bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 transition-all font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">WABA ID</label>
                    <input
                      type="text"
                      name="wabaId"
                      value={formData.wabaId}
                      onChange={handleChange}
                      placeholder="e.g. 123456789012345"
                      className={`w-full px-3.5 py-1.5 rounded-lg text-xs border bg-white focus:outline-none focus:ring-4 transition-all ${
                        fieldErrors.wabaId 
                          ? "border-red-500 focus:ring-red-500/10 focus:border-red-500" 
                          : "border-slate-200 focus:ring-emerald-500/5 focus:border-emerald-500"
                      }`}
                    />
                    {fieldErrors.wabaId && (
                      <div className="mt-1.5 flex items-center gap-1.5 px-2 py-1 bg-red-50 text-red-600 rounded-lg border border-red-100 animate-in fade-in slide-in-from-top-1 duration-200">
                        <Info size={12} className="shrink-0" />
                        <span className="text-[10px] font-bold leading-tight">{fieldErrors.wabaId}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Webhook Configuration Card */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all hover:border-slate-300/80">
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
              <div className="p-1.5 bg-indigo-100 rounded-lg text-indigo-600">
                <Link size={16} />
              </div>
              <h3 className="text-sm font-bold text-slate-800">Webhook Configuration</h3>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Callback URL</label>
                <div className="relative flex items-center">
                  <div className="absolute left-3 text-slate-400 pointer-events-none">
                    <Link size={13} />
                  </div>
                  <input
                    type="text"
                    name="webhookCallbackUrl"
                    value={formData.webhookCallbackUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/api/webhooks/whatsapp"
                    className="w-full pl-9 pr-4 py-1.5 rounded-lg text-xs border border-slate-200 bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Verify Token</label>
                  <input
                    type="text"
                    name="webhookVerifyToken"
                    value={formData.webhookVerifyToken}
                    onChange={handleChange}
                    placeholder="Enter verify token"
                    className="w-full px-3.5 py-1.5 rounded-lg text-xs border border-slate-200 bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 transition-all font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Webhook Secret</label>
                  <div className="relative flex items-center">
                    <input
                      type={showWebhookSecret ? "text" : "password"}
                      name="webhookSecret"
                      value={formData.webhookSecret}
                      onChange={handleChange}
                      placeholder="Enter secret"
                      className="w-full pl-3.5 pr-12 py-1.5 rounded-lg text-xs border border-slate-200 bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 transition-all font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      className="absolute right-3 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showWebhookSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <label className="relative inline-flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={!!formData.isActive}
                    onChange={handleToggle}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-500/10 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  <span className="ml-3 text-sm font-bold text-slate-700 select-none">Active</span>
                </label>
                <p className="text-[10px] text-slate-400 mt-2 ml-14">
                  Enable or disable all WhatsApp services for this workspace.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Senders (phone numbers) Management */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden transition-all hover:border-slate-300/80">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-emerald-600" />
                Connected WhatsApp Numbers
              </h3>
              <p className="text-[11px] text-slate-400 font-medium">
                Manage the WhatsApp numbers connected to this XploreByte workspace.
              </p>
              {hasReliableCapacityUsage(capacityPreflight) && (
                <p className="mt-1 text-[11px] font-semibold text-slate-600">
                  {capacityPreflight.used} of {capacityPreflight.maximum} numbers used
                </p>
              )}
            </div>

            {canManageWhatsApp && <div className="flex items-center gap-2">
              {showFetchButton && (
                <button
                  type="button"
                  onClick={handleFetchFromMeta}
                  disabled={fetchingMeta}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[11px] font-bold transition-all shadow-sm ${
                    fetchingMeta
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100 active:scale-95"
                  }`}
                >
                  <RefreshCw className={`w-3 h-3 ${fetchingMeta ? "animate-spin" : ""}`} />
                  {fetchingMeta ? "Syncing..." : "Sync from Meta"}
                </button>
              )}
              {esuStatus.isConfiguredViaEsu && (formData.wabaId || "").trim() ? (
                <button
                  type="button"
                  onClick={startAdditionalNumberEsu}
                  disabled={connectingAdditionalNumber || showAddNumberConfirmModal}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700 active:scale-95 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {connectingAdditionalNumber && (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  )}
                  {connectingAdditionalNumber ? "Checking..." : "Add Number"}
                </button>
              ) : !esuStatus.isConfiguredViaEsu ? (
                <button
                  type="button"
                  onClick={addSender}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-200 px-3.5 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 active:scale-95 transition-all shadow-sm"
                >
                  <Plus className="w-3 h-3" />
                  Add sender manually
                </button>
              ) : null}
            </div>}
          </div>

          <div className="p-1">
            {senders.length === 0 ? (
              <div className="bg-slate-50/50 rounded-2xl p-12 text-center border-2 border-dashed border-slate-100 m-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-slate-100 shadow-sm mb-4">
                  <Phone className="w-6 h-6 text-slate-300" />
                </div>
                <h4 className="text-slate-900 font-bold mb-1">No senders found</h4>
                <p className="text-slate-500 text-xs max-w-xs mx-auto mb-6 leading-relaxed">
                  Connect your Meta account or add your first sender manually to start using WhatsApp.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/40">
                      {[
                        "Display name",
                        "Phone number",
                        "Phone Number ID",
                        "Status",
                        "Default",
                        "Greeting",
                        "Actions",
                      ].map(column => (
                        <th
                          key={column}
                          scope="col"
                          className="whitespace-nowrap px-4 py-2.5 text-[9px] font-bold uppercase tracking-widest text-slate-400"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {senders.map((row, idx) => {
                      const rowKey = autoGreetingRowKey(row, idx);
                      const isAutoGreetingExpanded = expandedAutoGreetingKey === rowKey;
                      const displayName = row.label || row.verifiedName || "WhatsApp number";
                      const status = row.status || (row.isActive ? "Connected" : "Inactive");
                      const senderStatus = (row.status || "").trim().toUpperCase();
                      const canSetAsDefault = row.isActive &&
                        (!senderStatus || senderStatus === "CONNECTED");
                      const activeAlternatives = senders.filter(
                        (candidate, candidateIndex) => candidateIndex !== idx && candidate.isActive
                      );

                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            data-testid={`sender-row-${rowKey}`}
                            className={`align-middle transition-colors ${
                              row.isDefault ? "bg-emerald-50/25" : "bg-white hover:bg-slate-50/60"
                            }`}
                          >
                            <td className="px-4 py-3">
                              {esuStatus.isConfiguredViaEsu ? (
                                <span className="block max-w-[170px] truncate text-xs font-bold text-slate-900">
                                  {displayName}
                                </span>
                              ) : (
                                <input
                                  type="text"
                                  aria-label={`Display name for sender ${idx + 1}`}
                                  value={row.label || ""}
                                  onChange={e => updateSender(idx, "label", e.target.value)}
                                  placeholder="Display name"
                                  className="w-36 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs"
                                />
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-slate-700">
                              {esuStatus.isConfiguredViaEsu ? (
                                row.whatsAppBusinessNumber || "Pending from Meta"
                              ) : (
                                <input
                                  type="text"
                                  aria-label={`Phone number for sender ${idx + 1}`}
                                  value={row.whatsAppBusinessNumber || ""}
                                  onChange={e => updateSender(idx, "whatsAppBusinessNumber", e.target.value)}
                                  placeholder="WhatsApp number"
                                  className="w-40 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs"
                                />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {esuStatus.isConfiguredViaEsu ? (
                                <span
                                  title={row.phoneNumberId || "Phone Number ID unavailable"}
                                  className="block max-w-[175px] truncate font-mono text-[10px] text-slate-400"
                                >
                                  {row.phoneNumberId || "—"}
                                </span>
                              ) : (
                                <input
                                  type="text"
                                  aria-label={`Phone Number ID for sender ${idx + 1}`}
                                  value={row.phoneNumberId || ""}
                                  onChange={e => updateSender(idx, "phoneNumberId", e.target.value)}
                                  placeholder="Phone Number ID"
                                  className="w-44 rounded-lg border border-slate-200 px-2.5 py-1.5 font-mono text-[10px]"
                                />
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span className={`inline-flex rounded-md px-2 py-1 text-[9px] font-bold uppercase tracking-wide ${
                                senderStatus === "CONNECTED"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : row.isActive
                                    ? "bg-amber-50 text-amber-700"
                                    : "bg-slate-100 text-slate-500"
                              }`}>
                                {status.replaceAll("_", " ")}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {row.isDefault ? (
                                <span className="inline-flex rounded-md bg-emerald-100 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-emerald-700">
                                  Default
                                </span>
                              ) : canManageWhatsApp && canSetAsDefault && row.id ? (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await setDefaultNumber(formData.provider, row.id);
                                      await fetchNumbers(formData.provider);
                                      toast.success("Default WhatsApp number updated.");
                                    } catch (err) {
                                      toast.error(err?.response?.data?.message || "Could not set the default number.");
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[10px] font-bold text-amber-700 hover:bg-amber-50"
                                >
                                  <Star className="h-3 w-3" /> Set as default
                                </button>
                              ) : (
                                <span className="text-[10px] text-slate-300">—</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {canManageWhatsApp && (
                                <button
                                  type="button"
                                  onClick={() => toggleAutoGreetingPanel(row, idx)}
                                  aria-expanded={isAutoGreetingExpanded}
                                  aria-controls={`sender-settings-${rowKey}`}
                                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50"
                                >
                                  {isAutoGreetingExpanded ? (
                                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                                  )}
                                  Greeting
                                </button>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {canManageWhatsApp && (
                                <div className="flex items-center gap-1">
                                  {!esuStatus.isConfiguredViaEsu && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          const saved = await upsertNumber(formData.provider, row);
                                          setSenders(current => current.map((item, itemIndex) =>
                                            itemIndex === idx ? { ...item, id: saved?.id || item.id } : item
                                          ));
                                          toast.success("Sender saved.");
                                        } catch (err) {
                                          toast.error(err?.response?.data?.message || "Could not save sender.");
                                        }
                                      }}
                                      className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[10px] font-bold text-blue-700 hover:bg-blue-50"
                                    >
                                      <Save className="h-3 w-3" /> Save
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    aria-label={`Remove number ${displayName}`}
                                    title="Remove number"
                                    onClick={async () => {
                                      if (row.isDefault && activeAlternatives.length > 0) {
                                        toast.info(
                                          "This is your default WhatsApp number. Set another number as default before removing it."
                                        );
                                        return;
                                      }
                                      try {
                                        if (!row.id) {
                                          removeSender(idx);
                                          return;
                                        }
                                        await deleteNumber(formData.provider, row.id);
                                        await fetchNumbers(formData.provider);
                                        toast.success("WhatsApp number removed.");
                                      } catch (err) {
                                        const code = err?.response?.data?.code;
                                        toast.error(
                                          code === "DEFAULT_SENDER_REPLACEMENT_REQUIRED"
                                            ? "Set another WhatsApp number as default before removing this one."
                                            : err?.response?.data?.message || "Could not remove this number."
                                        );
                                      }
                                    }}
                                    className="inline-flex items-center justify-center rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {canManageWhatsApp && isAutoGreetingExpanded && (
                            <tr
                              id={`sender-settings-${rowKey}`}
                              data-testid={`sender-settings-${rowKey}`}
                              className="bg-slate-50/60"
                            >
                              <td colSpan={7} className="px-4 py-4">
                                <AutoGreetingSettingsPanel
                                  key={`${rowKey}-auto-greeting`}
                                  phoneNumberId={(row.phoneNumberId || "").trim()}
                                  provider={formData.provider}
                                  withBiz={withBiz}
                                  senderLabel={displayName}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl shadow-sm">
          <div className="mt-0.5 p-1.5 bg-white rounded-lg border border-blue-100 shadow-sm text-blue-500">
            <Info size={16} />
          </div>
          <p className="text-[11px] text-blue-700/80 leading-relaxed font-medium">
            The <b className="font-bold text-blue-900">Default</b> sender is critical—it will be used for automated messages like OTPs, welcome sequences, and when no specific phone number is selected during a mass broadcast.
          </p>
        </div>

        {testResult && (
          <div className="mt-8">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-slate-400" />
              Connection Test Result
            </h3>
            <pre className="text-xs bg-slate-900 text-emerald-400 p-6 rounded-2xl overflow-auto border border-slate-800 shadow-2xl max-h-[300px] font-mono leading-relaxed">
              {testResult}
            </pre>
          </div>
        )}
        {/* PIN Activation Modal */}
        <MetaPinActivationModal
          isOpen={showPinModal}
          onClose={closePinModal}
          businessId={businessId}
          phoneNumberId={pinPhoneNumberId}
          onSuccess={handlePinSuccess}
        />
        <WhatsAppCapacityLimitModal
          open={showCapacityLimitModal}
          onClose={() => setShowCapacityLimitModal(false)}
          preflight={capacityPreflight}
        />
        <WhatsAppAddNumberConfirmModal
          open={showAddNumberConfirmModal}
          preflight={capacityPreflight}
          onCancel={() => setShowAddNumberConfirmModal(false)}
          onContinue={continueAdditionalNumberEsu}
          isContinuing={connectingAdditionalNumber}
        />
      </div>
    </div>
  </div>
);
}
